// Site-wide timezone helpers. All stored times are UTC; display defaults to Central Time.
export const SITE_TZ = 'America/Chicago';

export const COMMON_TIMEZONES = [
  'America/Chicago', 'America/New_York', 'America/Denver', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'UTC',
];

const FRIENDLY = {
  'America/Chicago': 'Central Time', 'America/New_York': 'Eastern Time', 'America/Denver': 'Mountain Time',
  'America/Phoenix': 'Arizona Time', 'America/Los_Angeles': 'Pacific Time', 'America/Anchorage': 'Alaska Time',
  'Pacific/Honolulu': 'Hawaii Time', UTC: 'UTC',
};

export const allTimezones = () => {
  try {
    const list = Intl.supportedValuesOf('timeZone');
    return [...COMMON_TIMEZONES, ...list.filter((z) => !COMMON_TIMEZONES.includes(z))];
  } catch (e) {
    return COMMON_TIMEZONES;
  }
};

export const detectTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || SITE_TZ; } catch (e) { return SITE_TZ; }
};

const abbr = (tz, date = new Date()) => {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(date).find((p) => p.type === 'timeZoneName')?.value || '';
  } catch (e) { return ''; }
};

// "Central Time (CDT)"
export const tzLabel = (tz = SITE_TZ, date = new Date()) => {
  const name = FRIENDLY[tz] || tz.replace(/_/g, ' ');
  const a = abbr(tz, date);
  return a && a !== name ? `${name} (${a})` : name;
};

export const fmtTime = (iso, tz = SITE_TZ) =>
  new Date(iso).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });

export const fmtDate = (iso, tz = SITE_TZ, opts = { weekday: 'long', month: 'long', day: 'numeric' }) =>
  new Date(iso).toLocaleDateString('en-US', { timeZone: tz, ...opts });

export const fmtDateTime = (iso, tz = SITE_TZ) => `${fmtDate(iso, tz, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}, ${fmtTime(iso, tz)} ${tzLabel(tz, new Date(iso))}`;

// Calendar parts of an instant in a timezone.
export const zonedParts = (date, tz = SITE_TZ) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short',
  }).formatToParts(new Date(date));
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return {
    year: +get('year'), month: +get('month'), day: +get('day'), hour: +get('hour'), minute: +get('minute'),
    weekday: get('weekday'), key: `${get('year')}-${get('month')}-${get('day')}`,
  };
};

// Wall-clock time in tz -> real Date (UTC instant).
export const zonedToUtc = (year, month, day, hour, minute, tz = SITE_TZ) => {
  let guess = Date.UTC(year, month - 1, day, hour, minute);
  for (let i = 0; i < 3; i += 1) {
    const p = zonedParts(guess, tz);
    const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    guess += Date.UTC(year, month - 1, day, hour, minute) - asUtc;
  }
  return new Date(guess);
};

// Day key (YYYY-MM-DD) -> {year, month, day}
export const parseKey = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return { year: y, month: m, day: d };
};

export const addDaysKey = (key, n) => {
  const { year, month, day } = parseKey(key);
  const dt = new Date(Date.UTC(year, month - 1, day + n));
  return dt.toISOString().slice(0, 10);
};
